// src/components/LiveLogs/LiveLogs.js
/* eslint react-hooks/exhaustive-deps: 0 */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { format } from "date-fns";
import PropTypes from "prop-types";
import "./LiveLogs.css";
import {
  getVideoLogEventSource,
  getLiveVehicleCountEventSource,
  getAudioLogEventSource,
  getBounceAudioLogEventSource,
  PREDEFINED_MODES_MAP,
  LIVE_COUNT_SSE_CONFIG,
  getHistoricalLogs,
  getWaitingTimeStatisticsWebSocket,
} from "../Api";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import CustomAudioPlayer from "./CustomAudioPlayer";
const MAX_LOG_ENTRIES = 100;

// Multi-analysis mode field mappings (copied from HistoricalLogs)
const MODE_SPECIFIC_CONFIG_AUDIO = {
  fieldMappings: {
    'Customer_sentiment_analyzer': {
      primaryField: 'customer_sentiment',
      secondaryField: 'concern',
      tertiaryField: 'solution_provided',
      primaryLabel: 'Customer Sentiment',
      secondaryLabel: 'Concern',
      tertiaryLabel: 'Solution Provided'
    },
    'Referral_tracker': {
      primaryField: 'classification',
      secondaryField: 'evidence', 
      tertiaryField: 'confidence',
      primaryLabel: 'Classification',
      secondaryLabel: 'Evidence',
      tertiaryLabel: 'Confidence'
    },
    'Diversion_tracker': {
      primaryField: 'classification',
      secondaryField: 'evidence',
      tertiaryField: 'confidence',
      primaryLabel: 'Classification',
      secondaryLabel: 'Evidence',
      tertiaryLabel: 'Confidence'
    }
  }
};

// Utility function to detect analysis mode from log entry (copied from HistoricalLogs)
const detectAnalysisMode = (logEntry) => {
  if (!logEntry) return 'Customer_sentiment_analyzer';
  
  // Debug logging for mode detection
  const debugInfo = {
    hasAnalysisMode: !!logEntry.analysis_mode,
    analysisMode: logEntry.analysis_mode,
    hasClassification: logEntry.classification !== undefined,
    classification: logEntry.classification,
    hasCustomerSentiment: logEntry.customer_sentiment !== undefined,
    customerSentiment: logEntry.customer_sentiment
  };
  
  // Check for explicit analysis_mode field first
  if (logEntry.analysis_mode) {
    console.log("[LiveLogs] detectAnalysisMode - Using explicit analysis_mode:", debugInfo);
    return logEntry.analysis_mode;
  }
  
  // Check for classification field (Referral/Diversion modes)
  if (logEntry.classification !== undefined) {
    // Distinguish between referral and diversion based on classification values
    const classification = String(logEntry.classification).toLowerCase();
    console.log("[LiveLogs] detectAnalysisMode - Using classification field:", { ...debugInfo, classificationLower: classification });
    if (classification === 'referral' || classification === 'self') {
      return 'Referral_tracker';
    } else if (classification === 'diversion') {
      return 'Diversion_tracker';
    } else if (classification === 'neutral') {
      // Neutral can be used by both trackers - use explicit analysis_mode if available
      return logEntry.analysis_mode || 'Referral_tracker';
    }
    // Fallback for any other classification values
    return 'Referral_tracker';
  }
  
  // Check for customer_sentiment field (Customer sentiment mode)
  if (logEntry.customer_sentiment !== undefined) {
    console.log("[LiveLogs] detectAnalysisMode - Using customer_sentiment fallback:", debugInfo);
    return 'Customer_sentiment_analyzer';
  }
  
  // Default fallback
  console.log("[LiveLogs] detectAnalysisMode - Using default fallback:", debugInfo);
  return 'Customer_sentiment_analyzer';
};

// Get field mapping based on analysis mode (copied from HistoricalLogs)
const getFieldMapping = (logEntry, explicitMode = null) => {
  const mode = explicitMode || detectAnalysisMode(logEntry);

  if (MODE_SPECIFIC_CONFIG_AUDIO.fieldMappings && MODE_SPECIFIC_CONFIG_AUDIO.fieldMappings[mode]) {
    return MODE_SPECIFIC_CONFIG_AUDIO.fieldMappings[mode];
  }

  // Fallback to customer sentiment analyzer
  return MODE_SPECIFIC_CONFIG_AUDIO.fieldMappings['Customer_sentiment_analyzer'];
};

// Filter function to determine if a log entry should be displayed based on current analysis mode
const shouldDisplayLogEntry = (logEntry, currentAnalysisMode, isAudioMode) => {
  // Don't filter non-audio modes - show all entries
  if (!isAudioMode || !currentAnalysisMode) {
    return true;
  }

  // For audio modes, detect the log entry's analysis mode
  const logMode = detectAnalysisMode(logEntry);

  // Show entry only if modes match
  const shouldShow = logMode === currentAnalysisMode;

  // Debug logging for filtering decisions
  console.log(`[LiveLogs] shouldDisplayLogEntry:`, {
    currentAnalysisMode,
    logMode,
    shouldShow,
    logEntry: {
      analysis_mode: logEntry.analysis_mode,
      classification: logEntry.classification,
      customer_sentiment: logEntry.customer_sentiment,
      _logType: logEntry._logType
    }
  });

  return shouldShow;
};

const VIDEO_SSE_EVENT_PATTERNS = {
  "Footfall Tracker": "people_counting_log",
  "Traffic Analyzer": "vehicle_counting_lp_rec_log",
  "Queue Monitor": "queue_management_log",
  "Idle Employee Tracker": "idle_employee_counter_log",
  "Waiting Time Tracker": "waiting_time_log",
};

const AUDIO_SSE_EVENT_NAMES = {
    rtsp: "rtsp_audio_log",
    local_chunked: "local_audio_chunk_log",
    rtsp_bounce: "rtsp_bounce_audio_log",
    local_chunked_bounce: "local_bounce_audio_chunk_log",
    remote_chunked_bounce: "local_bounce_audio_chunk_log",
     websocket_audio_bounce: "rtsp_bounce_audio_log", // ← NEW
};

// ────────────────────────────────────────────────────────────
// Return a browser-playable link for the audio in a log entry
// ────────────────────────────────────────────────────────────
const getPlayableUrl = (entry) => {
  // Preferred: backend’s presigned HTTPS link
  if (entry.audio_url) return entry.audio_url;

  // Fallback: convert s3://bucket/key → https://bucket.s3.amazonaws.com/key
  if (entry.audio_s3_uri?.startsWith("s3://")) {
    const [, bucket, key] = entry.audio_s3_uri.match(/^s3:\/\/([^/]+)\/(.+)$/) || [];
    if (bucket && key) return `https://${bucket}.s3.amazonaws.com/${key}`;
  }
  return null;
};

/* ------------------------------------------------------------------ */
/* Thin helper so the rest of the file only has to pass a src string. */
const InlineAudioPlayer = ({ src }) => (
  <CustomAudioPlayer
    src={src}
    preload="none"                /* don't download audio until user clicks play */
    className="inline-audio-player" /* Add a class for targeted CSS styling */
  />
);
/* prop-type check keeps eslint happy */
InlineAudioPlayer.propTypes = { src: PropTypes.string.isRequired };
/* ------------------------------------------------------------------ */
const ExpandableSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="expandable-section">
      <button
        className="expandable-header"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <span className="expandable-icon">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </button>
      {isOpen && <div className="expandable-content">{children}</div>}
    </div>
  );
};
ExpandableSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
};


const LiveLogs = ({
  currentMode,
  analysisResult,
  videoInstanceId,
  audioStreamId,
  audioTaskType,
  ddbSourceId,
  currentAnalysisMode,
  enableHistoricalPolling = true,
}) => {
  const detailLogSseEventPattern = useMemo(
    () => VIDEO_SSE_EVENT_PATTERNS[currentMode],
    [currentMode]
  );
  const liveCountSseConfigForMode = useMemo(
    () => LIVE_COUNT_SSE_CONFIG[currentMode],
    [currentMode]
  );

  const isStreamingSentimentAudioMode =
    currentMode === "Customer Service Monitor" &&
    audioStreamId &&
    audioTaskType &&
    !audioTaskType.includes("_bounce");

  const isStreamingBounceAudioMode =
    currentMode === "Bounce Detector" &&
    audioStreamId &&
    audioTaskType &&
    audioTaskType.includes("_bounce");

  const isAnyStreamingAudioMode = isStreamingSentimentAudioMode || isStreamingBounceAudioMode;

  const isDedicatedVideoLogMode =
    videoInstanceId &&
    ddbSourceId &&
    currentMode !== "Customer Service Monitor" &&
    currentMode !== "Bounce Detector" &&
    detailLogSseEventPattern;

  const isStaticSentimentResultMode =
    currentMode === "Customer Service Monitor" &&
    !audioStreamId &&
    !videoInstanceId &&
    !!analysisResult &&
    analysisResult.analysisType === 'sentiment';

  const isStaticBounceResultMode =
    currentMode === "Bounce Detector" &&
    !audioStreamId &&
    !videoInstanceId &&
    !!analysisResult &&
    analysisResult.analysisType === 'bounce';

  const isAnyStaticAudioResultMode = isStaticSentimentResultMode || isStaticBounceResultMode;

  const isHistoricalAudioMode =
    (currentMode === "Customer Service Monitor" || currentMode === "Bounce Detector") &&
    ddbSourceId &&
    !audioStreamId &&
    !analysisResult;

  const [displayEntries, setDisplayEntries] = useState([]);
  const displayEntriesRef = useRef([]);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const [videoSseCounts, setVideoSseCounts] = useState({
    people: { entered: 0, exited: 0 },
    vehicles: { total: 0 },
    queue: { length: 0 },
    idle: { status: "UNKNOWN", duration: 0 },
  });
  const [waitingTimeStats, setWaitingTimeStats] = useState({
    overall: { total_people: 0, people_waiting_over_threshold: 0, total_entries: 0 },
    zones: [],
    metadata: { last_updated: null, camera_id: null },
    connected: false,
    connecting: false,
  });
  const [logStatusMessage, setLogStatusMessage] = useState("Initializing logs…");

  const mainLogEventSourceRef = useRef(null);
  const liveCountEventSourceRef = useRef(null);
  const waitingTimeStatsSocketRef = useRef(null);
  const idleTimerRef = useRef(null);
  const logsContainerRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    displayEntriesRef.current = displayEntries;
  }, [displayEntries]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (mainLogEventSourceRef.current) mainLogEventSourceRef.current.close();
      if (liveCountEventSourceRef.current) liveCountEventSourceRef.current.close();
      if (waitingTimeStatsSocketRef.current) waitingTimeStatsSocketRef.current.close();
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
  }, []);

  const fmt = (isoStr) => (isoStr ? format(new Date(isoStr), "HH:mm:ss") : "---");
  const fmtIdle = (sec) =>
    !Number.isFinite(sec) || sec < 0
      ? "0s"
      : sec < 60
      ? `${Math.floor(sec)}s`
      : sec < 3600
      ? `${Math.floor(sec / 60)}m ${Math.floor(sec % 60)}s`
      : `${Math.floor(sec / 3600)}h ${Math.floor((sec / 60) % 60)}m`;
  const fmtWaitTime = (sec) =>
    !Number.isFinite(sec) || sec < 0
      ? "0:00"
      : sec < 3600
      ? `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`
      : `${Math.floor(sec / 3600)}:${String(Math.floor((sec / 60) % 60)).padStart(2, '0')}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;

  const updateIdleStatusFromHistoricalLogs = useCallback((logEntries) => {
    if (currentMode !== "Idle Employee Tracker" || !logEntries || logEntries.length === 0) {
        return;
    }
    let lastRelevantEvent = null;
    for (let i = logEntries.length - 1; i >= 0; i--) {
        const entry = logEntries[i];
        if (entry._logType === "Idle Employee Tracker" && (entry.event === "idle_start" || entry.event === "idle_end")) {
            lastRelevantEvent = entry;
            break;
        }
    }
    if (lastRelevantEvent) {
        const newStatus = lastRelevantEvent.event === "idle_start" ? "IDLE" : "OCCUPIED";
        let newDuration = 0;
        if (newStatus === "IDLE") {
            const startTime = new Date(lastRelevantEvent.utc_timestamp || lastRelevantEvent.event_timestamp_utc || lastRelevantEvent._receivedAt).getTime();
            newDuration = (Date.now() - startTime) / 1000;
        } else {
            newDuration = lastRelevantEvent.idle_duration_seconds ?? 0;
        }
        newDuration = newDuration > 0 ? newDuration : 0;
        setVideoSseCounts(prev => {
            if (prev.idle.status !== newStatus ||
                (newStatus === "OCCUPIED" && prev.idle.duration !== newDuration) ||
                prev.idle.status === "UNKNOWN") {
                if (newStatus === "OCCUPIED" && idleTimerRef.current) {
                    clearInterval(idleTimerRef.current);
                    idleTimerRef.current = null;
                }
                return { ...prev, idle: { status: newStatus, duration: newDuration } };
            }
            return prev;
        });
    } else {
         if (videoSseCounts.idle.status === "UNKNOWN" &&
             (!mainLogEventSourceRef.current || mainLogEventSourceRef.current.readyState !== EventSource.OPEN)) {
             // console.log("[LiveLogs] No historical idle events for today, SSE not connected. Status remains UNKNOWN.");
         }
    }
  }, [currentMode, videoSseCounts.idle.status]);

  useEffect(() => {
    if (!mountedRef.current) return;

    setDisplayEntries([]);
    setLogStatusMessage("Initializing logs…");
    setIsLoadingHistorical(false);
    if (mainLogEventSourceRef.current) mainLogEventSourceRef.current.close();
    mainLogEventSourceRef.current = null;

    if (currentMode !== "Idle Employee Tracker" && videoSseCounts.idle.status !== "UNKNOWN") {
        setVideoSseCounts(p => ({...p, idle: {status: "UNKNOWN", duration: 0}}));
    }
    if (idleTimerRef.current && currentMode !== "Idle Employee Tracker") {
      clearInterval(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    // Cleanup waiting time statistics WebSocket when not in waiting time mode
    if (currentMode !== "Waiting Time Tracker") {
      if (waitingTimeStatsSocketRef.current) {
        waitingTimeStatsSocketRef.current.close();
        waitingTimeStatsSocketRef.current = null;
      }
      setWaitingTimeStats({
        overall: { total_people: 0, people_waiting_over_threshold: 0, total_entries: 0 },
        zones: [],
        metadata: { last_updated: null, camera_id: null },
        connected: false,
        connecting: false,
      });
    }

    const attachSse = () => {
      let sse = null;
      let eventName = null;
      let logType = currentMode;

      if (isAnyStreamingAudioMode && audioStreamId && audioTaskType) {
          if (isStreamingBounceAudioMode) {
              sse = getBounceAudioLogEventSource(audioStreamId);
              eventName = AUDIO_SSE_EVENT_NAMES[audioTaskType] || "bounce_audio_log";
              logType = audioTaskType.includes("rtsp") ? "RTSP Bounce Audio Chunk" : "Local Bounce Audio Chunk";
              if (audioTaskType.includes("remote_chunked")) logType = "Remote Bounce Audio Chunk";
          } else if (isStreamingSentimentAudioMode) {
              sse = getAudioLogEventSource(audioStreamId);
              eventName = AUDIO_SSE_EVENT_NAMES[audioTaskType] || "audio_log";
              logType = audioTaskType.includes("rtsp") ? "RTSP Audio Analysis Chunk" : "Local File Audio Chunk";
          }
      } else if (currentMode === "Queue Monitor" && videoInstanceId) {
        sse = getVideoLogEventSource(videoInstanceId);
        const backendModeName = PREDEFINED_MODES_MAP[currentMode];
        eventName = backendModeName
          ? `${backendModeName.toLowerCase().replace(/ & /g, "_and_").replace(/ /g, "_")}_log`
          : "queue_management_log";
        logType = "Queue Monitor";
      } else if (isDedicatedVideoLogMode && videoInstanceId) {
        sse = getVideoLogEventSource(videoInstanceId);
        const backendModeName = PREDEFINED_MODES_MAP[currentMode];
        eventName = backendModeName
          ? `${backendModeName.toLowerCase().replace(/ & /g, "_and_").replace(/ /g, "_")}_log`
          : detailLogSseEventPattern;
        logType = currentMode;
      }

      if (!sse || !eventName) {
        if (isAnyStreamingAudioMode || (isDedicatedVideoLogMode && videoInstanceId) || (currentMode === "Queue Monitor" && videoInstanceId)) {
            setLogStatusMessage("Could not establish live log connection. Check task and stream IDs.");
        }
        return;
      }
      mainLogEventSourceRef.current = sse;
      setLogStatusMessage((p) => `Connecting to live ${logType} updates…`);

      sse.onopen = () => {
        if (!mountedRef.current) return;
        setLogStatusMessage((prev) => prev.replace("Connecting to", "Connected to"));
        if (currentMode === "Idle Employee Tracker") {
            if (videoSseCounts.idle.status === "IDLE") {
                if (!idleTimerRef.current) {
                    const approxHistoricalStartTime = Date.now() - (videoSseCounts.idle.duration * 1000);
                    if (idleTimerRef.current) clearInterval(idleTimerRef.current);
                    idleTimerRef.current = setInterval(() => {
                        if (!mountedRef.current) return;
                        setVideoSseCounts((p) => ({
                            ...p,
                            idle: { ...p.idle, status: "IDLE", duration: (Date.now() - approxHistoricalStartTime) / 1000 },
                        }));
                    }, 1000);
                }
            }
            else if (videoSseCounts.idle.status === "UNKNOWN") {
                updateIdleStatusFromHistoricalLogs(displayEntriesRef.current);
            }
        }
      };

      sse.addEventListener(eventName, (msg) => {
        if (!mountedRef.current) return;
        console.log(`[LiveLogs] SSE event received - Event: ${eventName}, Mode: ${currentMode}, IsAudioMode: ${isAnyStreamingAudioMode}`);
        
        let data;
        try { data = JSON.parse(msg.data); }
        catch (_) { console.error("[LiveLogs] bad SSE data:", msg.data); return; }
        
        // Debug: Log all SSE data for audio events
        if (isAnyStreamingAudioMode) {
          console.log("[LiveLogs] Raw SSE data received:", {
            eventName,
            currentMode,
            data: {
              analysis_mode: data.analysis_mode,
              classification: data.classification,
              customer_sentiment: data.customer_sentiment,
              allKeys: Object.keys(data)
            }
          });
        } else {
          console.log(`[LiveLogs] Non-audio SSE event:`, { eventName, currentMode, dataKeys: Object.keys(data) });
        }

        if (currentMode === "Footfall Tracker" && "running_enter" in data) {
          setVideoSseCounts((p) => ({ ...p, people: { entered: data.running_enter, exited: data.running_exit } }));
        } else if (currentMode === "Queue Monitor" && "queue_count" in data) {
          setVideoSseCounts((p) => ({ ...p, queue: { length: data.queue_count } }));
        } else if (currentMode === "Idle Employee Tracker") {
          if (data.event === "idle_start") {
            if (idleTimerRef.current) clearInterval(idleTimerRef.current);
            const start = Date.now();
            idleTimerRef.current = setInterval(() => {
              if (!mountedRef.current) return;
              setVideoSseCounts((p) => ({
                ...p,
                idle: { status: "IDLE", duration: (Date.now() - start) / 1000 },
              }));
            }, 1000);
            setVideoSseCounts((p) => ({ ...p, idle: { status: "IDLE", duration: 0 } }));
          } else if (data.event === "idle_end") {
            if (idleTimerRef.current) clearInterval(idleTimerRef.current);
            idleTimerRef.current = null;
            setVideoSseCounts((p) => ({
              ...p,
              idle: { status: "OCCUPIED", duration: data.idle_duration_seconds ?? p.idle.duration },
            }));
          }
        }

        const shouldAppendToDisplay = 
          currentMode === "Queue Monitor" || 
          isAnyStreamingAudioMode; // Allow audio events to be displayed
        if (!shouldAppendToDisplay) return;

        const entry = {
          ...data,
          _logType: logType,
          _receivedAt: new Date().toISOString(),
          _isHistorical: false,
        };
        
        // Debug: Log entry processing for audio events
        if (isAnyStreamingAudioMode) {
          const detectedMode = detectAnalysisMode(entry);
          const fieldMapping = getFieldMapping(entry);
          console.log("[LiveLogs] Entry being added to display:", {
            entry: {
              analysis_mode: entry.analysis_mode,
              classification: entry.classification,
              customer_sentiment: entry.customer_sentiment,
              _logType: entry._logType,
              _isHistorical: entry._isHistorical
            },
            detectedMode,
            fieldMapping
          });
        }

        // Apply analysis mode filtering for audio entries
        const shouldShow = shouldDisplayLogEntry(entry, currentAnalysisMode, isAnyStreamingAudioMode);
        if (!shouldShow) {
          console.log("[LiveLogs] Filtering out SSE entry due to analysis mode mismatch");
          return;
        }

        setDisplayEntries((prev) => [...prev, entry].slice(-MAX_LOG_ENTRIES));
      });

      sse.addEventListener("status", (m) => {
        if (!mountedRef.current) return;
        try {
          const d = JSON.parse(m.data);
          if (d.message) {
            setLogStatusMessage(d.message);
            if (currentMode === "Idle Employee Tracker" && (d.message.toLowerCase().includes("stopped") || d.message.toLowerCase().includes("not active"))) {
                 if (idleTimerRef.current) clearInterval(idleTimerRef.current);
                 idleTimerRef.current = null;
                 setVideoSseCounts(prev => {
                    if (prev.idle.status !== "DISCONNECTED") {
                        return { ...prev, idle: { status: "DISCONNECTED", duration: prev.idle.duration } };
                    }
                    return prev;
                 });
            }
          }
        } catch (_) {}
      });

      sse.onerror = () => {
        if (!mountedRef.current) return;
        setLogStatusMessage("Live SSE connection error.");
        if (currentMode === "Idle Employee Tracker") {
          if (idleTimerRef.current) clearInterval(idleTimerRef.current);
          idleTimerRef.current = null;
          setVideoSseCounts(prev => {
            if (prev.idle.status !== "DISCONNECTED") {
                return { ...prev, idle: { status: "DISCONNECTED", duration: prev.idle.duration } };
            }
            return prev;
          });
        }
        if (mainLogEventSourceRef.current) {
          mainLogEventSourceRef.current.close();
          mainLogEventSourceRef.current = null;
        }
      };
    };

    const attachWaitingTimeStatsWebSocket = () => {
      if (currentMode !== "Waiting Time Tracker" || !ddbSourceId) {
        console.log(`[LiveLogs] Skipping WebSocket connection. Mode: ${currentMode}, ddbSourceId: ${ddbSourceId}`);
        return;
      }

      console.log(`[LiveLogs] Attempting to connect WebSocket for camera: ${ddbSourceId}`);
      setLogStatusMessage("Connecting to live waiting time statistics…");
      setWaitingTimeStats(prev => ({ ...prev, connecting: true, connected: false }));
      
      try {
        const ws = getWaitingTimeStatisticsWebSocket(ddbSourceId);
        if (!ws) {
          console.error("[LiveLogs] WebSocket creation failed - getWaitingTimeStatisticsWebSocket returned null");
          setLogStatusMessage("Could not establish waiting time statistics connection.");
          setWaitingTimeStats(prev => ({ ...prev, connecting: false, connected: false }));
          return;
        }

        waitingTimeStatsSocketRef.current = ws;

        ws.onopen = () => {
          if (!mountedRef.current) return;
          console.log("[LiveLogs] WebSocket connection opened successfully");
          setLogStatusMessage("Connected to live waiting time statistics.");
          setWaitingTimeStats(prev => ({ ...prev, connected: true, connecting: false }));
        };

        ws.onmessage = (event) => {
          if (!mountedRef.current) return;
          console.log("[LiveLogs] Raw WebSocket message received:", event.data);
          
          try {
            const data = JSON.parse(event.data);
            console.log("[LiveLogs] Parsed WebSocket data:", data);
            
            // Handle heartbeat messages
            if (data.type === "heartbeat") {
              console.log("[LiveLogs] Heartbeat message received, ignoring");
              return;
            }
            
            // Handle statistics messages (both initial and updates)
            if (data.type === "statistics_update" || data.type === "initial_statistics") {
              console.log(`[LiveLogs] Statistics message detected (${data.type})`);
              console.log("[LiveLogs] Full data object:", JSON.stringify(data, null, 2));
              
              if (!data.statistics) {
                console.error("[LiveLogs] No statistics object found in data:", data);
                return;
              }
              
              const stats = data.statistics;
              console.log("[LiveLogs] Statistics object:", JSON.stringify(stats, null, 2));
              
              // Helper function to convert time string (MM:SS or HH:MM:SS) to seconds
              const timeStringToSeconds = (timeStr) => {
                console.log(`[LiveLogs] timeStringToSeconds called with:`, timeStr, typeof timeStr);
                
                if (!timeStr) {
                  console.log("[LiveLogs] Time string is null/undefined");
                  return 0;
                }
                
                if (typeof timeStr !== 'string') {
                  console.log("[LiveLogs] Time string is not a string, converting:", timeStr);
                  timeStr = String(timeStr);
                }
                
                const parts = timeStr.split(':').map(part => {
                  const num = parseInt(part, 10);
                  console.log(`[LiveLogs] Parsing time part: "${part}" -> ${num}`);
                  return isNaN(num) ? 0 : num;
                });
                
                console.log(`[LiveLogs] Time parts array:`, parts);
                
                let seconds = 0;
                if (parts.length === 2) {
                  // MM:SS format - handle large minute values
                  seconds = parts[0] * 60 + parts[1];
                  console.log(`[LiveLogs] Converted ${timeStr} (MM:SS) to ${seconds} seconds (${Math.floor(seconds/60)}m ${seconds%60}s)`);
                } else if (parts.length === 3) {
                  // HH:MM:SS format
                  seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
                  console.log(`[LiveLogs] Converted ${timeStr} (HH:MM:SS) to ${seconds} seconds (${Math.floor(seconds/3600)}h ${Math.floor((seconds%3600)/60)}m ${seconds%60}s)`);
                } else {
                  console.warn(`[LiveLogs] Unrecognized time format: ${timeStr}, parts:`, parts);
                  return 0;
                }
                
                return seconds;
              };
              
              // Transform the data to match expected format
              console.log("[LiveLogs] Processing overall statistics from:", {
                total_people: stats.total_people,
                waiting_over_1min: stats.waiting_over_1min,
                total_entries: stats.total_entries
              });
              
              console.log("[LiveLogs] Processing zones array:", stats.zones);
              console.log("[LiveLogs] Zones array length:", stats.zones ? stats.zones.length : 'undefined');
              
              const transformedData = {
                overall: {
                  total_people: stats.total_people || 0,
                  people_waiting_over_threshold: stats.waiting_over_1min || 0,
                  total_entries: stats.total_entries || 0
                },
                zones: (stats.zones || []).map((zone, index) => {
                  console.log(`[LiveLogs] Processing zone ${index}:`, zone);
                  
                  const transformedZone = {
                    zone_id: zone.zone_id || index + 1,
                    zone_name: zone.zone_name || `Zone ${zone.zone_id || index + 1}`,
                    current_people: zone.current_people || 0,
                    total_entries: zone.total_entries || 0,
                    waiting_over_1min: zone.waiting_over_1min || 0,
                    current_average_wait_time_seconds: timeStringToSeconds(zone.current_avg_time),
                    average_wait_time_since_start_seconds: timeStringToSeconds(zone.avg_since_start),
                    status: zone.status || 'UNKNOWN',
                    max_wait_time: zone.max_wait_time,
                    max_wait_time_seconds: timeStringToSeconds(zone.max_wait_time)
                  };
                  console.log(`[LiveLogs] Transformed zone ${transformedZone.zone_id}:`, JSON.stringify(transformedZone, null, 2));
                  return transformedZone;
                }),
                metadata: {
                  last_updated: data.timestamp || new Date().toISOString(),
                  camera_id: data.camera_id || ddbSourceId,
                  camera_name: data.camera_name,
                  session_duration: data.session_duration,
                  data_age_seconds: data.data_age_seconds,
                  is_fresh: data.is_fresh
                },
                connected: true,
              };
              
              console.log("[LiveLogs] Final transformed data:");
              console.log("  - Overall:", transformedData.overall);
              console.log("  - Zones count:", transformedData.zones.length);
              console.log("  - Zones:", JSON.stringify(transformedData.zones, null, 2));
              console.log("  - Metadata:", transformedData.metadata);
              
              setWaitingTimeStats(transformedData);
              console.log("[LiveLogs] setWaitingTimeStats called with transformed data");
            } else {
              console.warn("[LiveLogs] Received message that is not statistics_update or initial_statistics:", data);
            }
          } catch (error) {
            console.error("[LiveLogs] Error parsing waiting time statistics data:", error);
            console.error("[LiveLogs] Error stack:", error.stack);
            console.error("[LiveLogs] Raw data that caused error:", event.data);
          }
        };

        ws.onerror = (error) => {
          if (!mountedRef.current) return;
          console.error("[LiveLogs] WebSocket error:", error);
          setLogStatusMessage("Waiting time statistics connection error.");
          setWaitingTimeStats(prev => ({ ...prev, connected: false, connecting: false }));
        };

        ws.onclose = (event) => {
          if (!mountedRef.current) return;
          console.log("[LiveLogs] WebSocket connection closed. Code:", event.code, "Reason:", event.reason);
          setLogStatusMessage("Waiting time statistics connection closed.");
          setWaitingTimeStats(prev => ({ ...prev, connected: false, connecting: false }));
          waitingTimeStatsSocketRef.current = null;
        };
      } catch (error) {
        console.error("[LiveLogs] Error creating waiting time statistics WebSocket:", error);
        setLogStatusMessage("Failed to create waiting time statistics connection.");
      }
    };

    const ddbModeMap = {
      "Footfall Tracker": "footfall",
      "Idle Employee Tracker": "idle",
      "Traffic Analyzer": "vehicle",
      "Customer Service Monitor": "audio",
      "Waiting Time Tracker": "waiting_time",
      "Bounce Detector": "bounce",
    };
    const ddbMode = ddbModeMap[currentMode];

    const fetchHistorical = async () => {
      if (!ddbMode || !ddbSourceId) {
        if (currentMode !== "No Active Process" && !isAnyStaticAudioResultMode) {
           setLogStatusMessage( prev => prev === "Initializing logs…" ? "Source ID for historical logs is missing." : prev);
        }
        return;
      }
      setIsLoadingHistorical(true);
      setLogStatusMessage("Fetching historical logs…");
      try {
        const response = await getHistoricalLogs(currentMode, ddbSourceId, null, null, 100, 0, 'desc');
        if (!mountedRef.current) return;
        // Handle both new paginated response and legacy array response (backward compatibility)
        const rows = response.items || response || [];
        const formatted = rows
          .map((r) => ({
            ...r,
            _logType: currentMode,
            _receivedAt:
              r.event_timestamp_utc ||
              r.timestamp_utc ||
              r.timestamp ||
              r.cross_utc ||
              r.utc_timestamp,
            _isHistorical: true,
          }))
          .sort((a, b) => new Date(a._receivedAt) - new Date(b._receivedAt))
          .slice(-MAX_LOG_ENTRIES);

        // Apply analysis mode filtering for audio historical logs
        const isAudioModeForHistorical = currentMode === "Customer Service Monitor" || currentMode === "Bounce Detector";
        const filteredHistorical = isAudioModeForHistorical && currentAnalysisMode
          ? formatted.filter(entry => shouldDisplayLogEntry(entry, currentAnalysisMode, isAudioModeForHistorical))
          : formatted;

        console.log(`[LiveLogs] fetchHistorical: Total entries: ${formatted.length}, Filtered entries: ${filteredHistorical.length}, Mode: ${currentMode}, Analysis Mode: ${currentAnalysisMode}`);

        setDisplayEntries(filteredHistorical);
        if (currentMode === "Idle Employee Tracker") {
            updateIdleStatusFromHistoricalLogs(formatted);
        }
        setLogStatusMessage(rows.length ? "Historical logs loaded." : "No historical logs for today or this source.");
      } catch (err) {
        if (mountedRef.current) setLogStatusMessage(`Error fetching historical logs: ${err.message}`);
      } finally {
        if (mountedRef.current) setIsLoadingHistorical(false);
      }
    };

    const needsDdbFetch = currentMode !== "Queue Monitor";
    // const needsSseConnection = isAnyStreamingAudioMode || currentMode === "Queue Monitor" || (isDedicatedVideoLogMode && videoInstanceId && detailLogSseEventPattern);

    const needsSseConnection =
   currentMode === "Queue Monitor" ||               // live queue logs
   (isDedicatedVideoLogMode && videoInstanceId &&   // still needed for
    detailLogSseEventPattern);  

    (async () => {
      if (needsDdbFetch) await fetchHistorical();
      if (needsSseConnection) {
        attachSse();
      } else if (currentMode === "Idle Employee Tracker") {
          if (displayEntriesRef.current.length > 0 && videoSseCounts.idle.status === "UNKNOWN") {
            updateIdleStatusFromHistoricalLogs(displayEntriesRef.current);
          }
      } else if (!isAnyStaticAudioResultMode && !isHistoricalAudioMode && !isLoadingHistorical && currentMode !== "No Active Process") {
          if (logStatusMessage === "Initializing logs…" || logStatusMessage === "Historical logs loaded." || logStatusMessage.startsWith("No historical logs")) {
            setLogStatusMessage("Provide necessary IDs (e.g., Video Instance ID) for live updates or check configuration.");
          }
      }

      // Connect to waiting time statistics WebSocket if in waiting time mode
      if (currentMode === "Waiting Time Tracker" && ddbSourceId) {
        attachWaitingTimeStatsWebSocket();
      }
    })();
  }, [
    currentMode,
    videoInstanceId,
    audioStreamId,
    audioTaskType,
    ddbSourceId,
    detailLogSseEventPattern,
    updateIdleStatusFromHistoricalLogs,
    videoSseCounts.idle.status,
  ]);

  useEffect(() => {
    if (currentMode === "Queue Monitor" || !ddbSourceId || currentMode === "No Active Process" || !enableHistoricalPolling) return undefined;

    const poll = async () => {
      if (!mountedRef.current) return;
      try {
        const response = await getHistoricalLogs(currentMode, ddbSourceId, null, null, 100, 0, 'desc');
        if (!mountedRef.current) return;
        // Handle both new paginated response and legacy array response (backward compatibility)
        const rows = response.items || response || [];
        if (!rows?.length) return;

        const knownTimestamps = new Set(
          displayEntriesRef.current.map(
            (e) => e.event_timestamp_utc || e.utc_timestamp || e.timestamp || e.cross_utc || e.utc_timestamp
          )
        );
        const fresh = rows.filter(r => !knownTimestamps.has(r.event_timestamp_utc || r.utc_timestamp || r.timestamp || r.cross_utc || r.utc_timestamp));

        if (!fresh.length) return;

        const newEntriesForDisplay = fresh.map((r) => ({
            ...r,
            _logType: currentMode,
            _receivedAt: r.event_timestamp_utc || r.timestamp_utc || r.timestamp || r.cross_utc || r.utc_timestamp,
            _isHistorical: true,
          }));

        // Apply analysis mode filtering for audio polling updates
        const isAudioModeForPolling = currentMode === "Customer Service Monitor" || currentMode === "Bounce Detector";
        const filteredNewEntries = isAudioModeForPolling && currentAnalysisMode
          ? newEntriesForDisplay.filter(entry => shouldDisplayLogEntry(entry, currentAnalysisMode, isAudioModeForPolling))
          : newEntriesForDisplay;

        console.log(`[LiveLogs] DDB Polling: New entries: ${newEntriesForDisplay.length}, Filtered entries: ${filteredNewEntries.length}, Mode: ${currentMode}, Analysis Mode: ${currentAnalysisMode}`);

        const combinedForDisplayAndStatus = [
            ...displayEntriesRef.current,
            ...filteredNewEntries,
          ].sort((a, b) => new Date(a._receivedAt) - new Date(b._receivedAt))
           .slice(-MAX_LOG_ENTRIES);

        setDisplayEntries(combinedForDisplayAndStatus);
        if (currentMode === "Idle Employee Tracker") {
            updateIdleStatusFromHistoricalLogs(combinedForDisplayAndStatus);
        }
      } catch (err) {
        console.error("[LiveLogs] DDB poll error", err);
      }
    };
    const id = setInterval(poll, 20_000);
    return () => clearInterval(id);
  }, [currentMode, ddbSourceId, updateIdleStatusFromHistoricalLogs, enableHistoricalPolling]);

  useEffect(() => {
    if (liveCountEventSourceRef.current) liveCountEventSourceRef.current.close();
    liveCountEventSourceRef.current = null;

    if (currentMode !== "Traffic Analyzer" && videoSseCounts.vehicles.total !== 0) {
      setVideoSseCounts((p) => ({ ...p, vehicles: { total: 0 } }));
    }

    if (currentMode === "Traffic Analyzer" && liveCountSseConfigForMode && videoInstanceId) {
      const es = getLiveVehicleCountEventSource();
      if (!es) return;
      liveCountEventSourceRef.current = es;
      es.addEventListener(liveCountSseConfigForMode.eventName, (msg) => {
        if (!mountedRef.current) return;
        try {
          const d = JSON.parse(msg.data);
          if ("live_vehicle_count" in d) {
            setVideoSseCounts((p) => ({ ...p, vehicles: { total: d.live_vehicle_count } }));
          }
        } catch (_) {}
      });
      es.onerror = () => {
        if (mountedRef.current) { setVideoSseCounts((p) => ({ ...p, vehicles: { total: "N/A" } })); }
        if (liveCountEventSourceRef.current) {
            liveCountEventSourceRef.current.close();
            liveCountEventSourceRef.current = null;
        }
      };
    }
  }, [currentMode, liveCountSseConfigForMode, videoInstanceId]);

  useEffect(() => {
    if (logsContainerRef.current && displayEntries.length) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [displayEntries]);


  const renderStaticAudioAnalysis = () => {
    if (!analysisResult) return <p className="log-placeholder">No static audio analysis result.</p>;
    if (analysisResult.error) return <div className="log-entry-item audio-analysis-log error-log"><strong>Error:</strong> {analysisResult.error}</div>;

    const ts = analysisResult.timestamp_utc || analysisResult.timestamp || (analysisResult.chunk_info ? analysisResult.chunk_info.start_time_utc : null) || new Date().toISOString();
    
    // Use dynamic field mapping instead of hardcoded bounce detection
    const fieldMapping = getFieldMapping(analysisResult);
    const isBounceResult = analysisResult.analysisType === 'bounce';
    const resultClass = isBounceResult ? "static-bounce-result" : "static-audio-result";
    
    // Get dynamic field values
    const primaryValue = analysisResult[fieldMapping.primaryField];
    const secondaryValue = analysisResult[fieldMapping.secondaryField];
    const tertiaryValue = analysisResult[fieldMapping.tertiaryField];

    return (
      <div className={`log-entry-item audio-analysis-log structured-audio-result ${resultClass}`}>
        <div className="audio-analysis-summary">
          <p><strong>Analysis Complete:</strong> {format(new Date(ts), "yyyy-MM-dd HH:mm:ss")}</p>
          <p>
            <strong>{fieldMapping.primaryLabel}:</strong>{" "}
            <span className={`sentiment-value sentiment-${(primaryValue || "").toLowerCase().replace(/\s+/g, "-")}`}>
              {primaryValue || "N/A"}
            </span>
          </p>
        </div>
        <ExpandableSection title={fieldMapping.secondaryLabel} defaultOpen>
          <p className="expandable-text-content">{secondaryValue || "N/A"}</p>
        </ExpandableSection>
        <ExpandableSection title={fieldMapping.tertiaryLabel}>
          <p className="expandable-text-content">{tertiaryValue || "N/A"}</p>
        </ExpandableSection>
        {analysisResult.transcript && (
          <ExpandableSection title="Full Transcript" defaultOpen>
            <p className="expandable-text-content transcript-text">{analysisResult.transcript}</p>
          </ExpandableSection>
        )}

         {getPlayableUrl(analysisResult) && (
          <ExpandableSection title="Play Audio" defaultOpen>
            <InlineAudioPlayer src={getPlayableUrl(analysisResult)} />
          </ExpandableSection>
        )}
      </div>
    );
  };

  const renderSingleDisplayEntry = useCallback(
    (entry, idx) => {
      const logEntryType = entry._logType || currentMode;
      const ts = entry.event_timestamp_utc || entry.timestamp_utc || entry.timestamp || entry.utc_timestamp || entry.chunk_info?.start_time_utc || entry.cross_utc || entry._receivedAt;
      const histCls = entry._isHistorical ? "historical-log-entry" : "live-log-entry";

      const isBounceLog = logEntryType.toLowerCase().includes("bounce") || (entry.source_type && entry.source_type.toLowerCase().includes("bounce"));

      if (
        logEntryType === "RTSP Audio Analysis Chunk" ||
        logEntryType === "Local File Audio Chunk" ||
        logEntryType === "RTSP Bounce Audio Chunk" ||
        logEntryType === "Local Bounce Audio Chunk" ||
        logEntryType === "Remote Bounce Audio Chunk" ||
        ((logEntryType === "Customer Service Monitor" || logEntryType === "Bounce Detector") && entry.source_type?.includes("chunk"))
      ) {
        const label = entry.chunk_info
          ? `Chunk ${entry.chunk_info.index}/${entry.chunk_info.total_chunks}`
          : entry.source_type || (isBounceLog ? "Bounce Segment" : "Audio Segment");
        
        // Use dynamic field mapping instead of hardcoded field detection
        const fieldMapping = getFieldMapping(entry);
        const analysisMode = detectAnalysisMode(entry);
        
        // Debug logging to verify correct field mapping
        if (!entry._isHistorical) {
          console.log(`[LiveLogs] Live event field mapping:`, {
            analysisMode,
            fieldMapping,
            entryData: { 
              analysis_mode: entry.analysis_mode,
              classification: entry.classification,
              customer_sentiment: entry.customer_sentiment 
            }
          });
        }
        
        // Get dynamic field values
        const primaryValue = entry[fieldMapping.primaryField];
        const secondaryValue = entry[fieldMapping.secondaryField];
        const tertiaryValue = entry[fieldMapping.tertiaryField];
        
        let entryClass = isBounceLog ? "bounce-chunk-log" : "sentiment-chunk-log";
        if (logEntryType.toLowerCase().includes("rtsp")) entryClass += " rtsp-chunk-log";
        else if (logEntryType.toLowerCase().includes("local")) entryClass += " local-chunk-log";
        else if (logEntryType.toLowerCase().includes("remote")) entryClass += " remote-chunk-log";
        

        return (
          <div key={`${ts}-${idx}-audio`} className={`log-entry-item audio-analysis-log structured-audio-result ${histCls} ${entryClass}`}>
            <div className="audio-analysis-summary">
              <p><strong>{label}</strong> @ {fmt(ts)}</p>
              <p>
                <strong>{fieldMapping.primaryLabel}:</strong>{" "}
                <span className={`sentiment-value sentiment-${(primaryValue || "").toLowerCase().replace(/\s+/g, "-")}`}>
                  {primaryValue || "N/A"}
                </span>
              </p>
            </div>
            {secondaryValue && (
              <ExpandableSection title={fieldMapping.secondaryLabel}>
                <p className="expandable-text-content">{secondaryValue}</p>
              </ExpandableSection>
            )}
            {tertiaryValue && (
              <ExpandableSection title={fieldMapping.tertiaryLabel}>
                <p className="expandable-text-content">{tertiaryValue}</p>
              </ExpandableSection>
            )}
            {entry.transcript && (
              <ExpandableSection title="Transcript (Chunk)">
                <p className="expandable-text-content transcript-text">{entry.transcript}</p>
              </ExpandableSection>
            )}

          {getPlayableUrl(entry) && (
              <ExpandableSection title="Play Audio" defaultOpen>
                <InlineAudioPlayer src={getPlayableUrl(entry)} />
              </ExpandableSection>
            )}


            {entry.error && <p className="error-log expandable-text-content">Error: {entry.error}</p>}
          </div>
        );
      }

      if (logEntryType === "Footfall Tracker") {
        return (
          <div key={`${ts}-${idx}-foot`} className={`log-entry-item people-log ${histCls}`}>
            <span className="log-time">{fmt(ts)}</span>{" "}
            <span className="log-event">{entry.event?.toUpperCase()}</span>{" "}
            (ID {entry.track_id ?? entry.person_id ?? "N/A"}) Run: In{" "}
            {entry.running_enter_count ?? entry.running_enter ?? "N/A"} Out{" "}
            {entry.running_exit_count ?? entry.running_exit ?? "N/A"}
          </div>
        );
      }

      if (logEntryType === "Traffic Analyzer") {
        const etime = entry.cross_utc || entry.timestamp_utc || entry.event_timestamp_utc || ts;
        let desc = `VEHICLE ${entry.car_id ?? "N/A"}`;
        if (entry.event_type === "vehicle_crossed" || entry.event_type === "vehicle_crossed_line")
          desc += " CROSSED";
        else if (
          entry.event_type === "vehicle_lp_detected" ||
          entry.event_type === "vehicle_lp_detected_delayed"
        ) {
          desc += " LP DETECTED";
          if (entry.event_type === "vehicle_lp_detected_delayed") desc += " (Delayed)";
        } else if (entry.lp_text) desc += " (LP DETECTED)";
        return (
          <div key={`${etime}-${idx}-veh`} className={`log-entry-item vehicle-log ${histCls}`}>
            <span className="log-time">{fmt(etime)}</span> {desc}
            {entry.lp_text && <span className="log-lp"> LP: {entry.lp_text}</span>}
            {entry.message && !entry.lp_text && (
              <span className="log-message"> ({entry.message})</span>
            )}
          </div>
        );
      }

      if (logEntryType === "Queue Monitor") {
        return (
          <div key={`${ts}-${idx}-queue`} className={`log-entry-item queue-log ${histCls}`}>
            <span className="log-time">{fmt(ts)}</span> QUEUE LEN: {entry.queue_count}{" "}
            {entry.avg_wait_time !== undefined && (
              <span className="log-wait"> AVG WAIT: {entry.avg_wait_time.toFixed(1)}s</span>
            )}
          </div>
        );
      }

      if (logEntryType === "Idle Employee Tracker") {
        const start = entry.event === "idle_start";
        const ets = entry.utc_timestamp || entry.event_timestamp_utc || ts;
        return (
          <div
            key={`${ets}-${idx}-idle`}
            className={`log-entry-item idle-log ${histCls} ${start ? "idle-start" : "idle-end"}`}
          >
            <span className="log-time">{fmt(ets)}</span>{" "}
            <span className={`log-event event-${entry.event}`}>
              AREA {start ? "EMPTY" : "OCCUPIED"}
            </span>
            {!start && entry.idle_duration_seconds !== undefined && (
              <span className="log-duration">
                (Idle {fmtIdle(entry.idle_duration_seconds)})
              </span>
            )}
          </div>
        );
      }

      const copy = { ...entry };
      delete copy._logType; delete copy._receivedAt; delete copy._isHistorical;
      return (
        <div key={`${ts}-${idx}-gen`} className={`log-entry-item generic-log ${histCls}`}>
          <span className="log-time">{fmt(ts)}</span>{" "}
          <pre>{JSON.stringify(copy, null, 2)}</pre>
        </div>
      );
    },
    [currentMode, fmt, fmtIdle]
  );

  let headerText = `${currentMode} Events`;
  let mainContent;

  if (currentMode === "No Active Process" && !isAnyStaticAudioResultMode) {
    headerText = `Events Log (${format(new Date(), "MMMM d, HH:mm")})`;
    mainContent = <p className="log-placeholder">No process active. Select a mode and source to start.</p>;
  } else if (isAnyStaticAudioResultMode) {
    const resultType = analysisResult?.analysisType === 'bounce' ? "Bounce Detector" : "Audio Sentiment";
    headerText = `${resultType} Result (Single Pass)`;
    mainContent = renderStaticAudioAnalysis();
  } else if (
    (isHistoricalAudioMode || (ddbSourceId && !videoInstanceId && !isAnyStreamingAudioMode
      && currentMode !== "No Active Process"
      && currentMode !== "Queue Monitor")) && 
    !isLoadingHistorical &&
    displayEntries.length === 0 &&
    (!mainLogEventSourceRef.current || mainLogEventSourceRef.current.readyState !== EventSource.OPEN)
  ) {
    headerText = `Historical Logs: ${currentMode}`;
    mainContent = <p className="log-placeholder">{logStatusMessage || "No historical logs for today or selected source."}</p>;
  } else if (isLoadingHistorical) {
    mainContent = <p className="log-placeholder">{logStatusMessage || "Loading historical logs…"}</p>;
  } else if (displayEntries.length > 0) {
    headerText = `Events: ${currentMode}`;
    mainContent = displayEntries.map(renderSingleDisplayEntry);
  } else if (logStatusMessage && (isAnyStreamingAudioMode || isDedicatedVideoLogMode || isHistoricalAudioMode || isLoadingHistorical || currentMode !== "No Active Process")) {
    mainContent = <p className="log-placeholder">{logStatusMessage}</p>;
  } else {
    mainContent = <p className="log-placeholder">Waiting for events or no historical data for: {currentMode}. Check configuration and IDs.</p>;
  }


  return (
    <div className="logs-panel card-style">
      {isDedicatedVideoLogMode && detailLogSseEventPattern && !isAnyStreamingAudioMode && (
        <div className="log-summaries-section">
           {currentMode === "Footfall Tracker" && (
            <div className="log-counts people-counts">
                <h3>Live People Counts</h3>
                <p>
                    In: <span className="count-value">{videoSseCounts.people.entered}</span> Out:{" "}
                    <span className="count-value">{videoSseCounts.people.exited}</span>
                </p>
            </div>
            )}
           {currentMode === "Traffic Analyzer" && (
            <div className="log-counts vehicle-counts">
                <h3>Live Vehicle Count</h3>
                <p>Total: <span className="count-value">{videoSseCounts.vehicles.total}</span></p>
            </div>
            )}
           {currentMode === "Queue Monitor" && (
            <div className="log-counts queue-counts">
                <h3>Live Queue Length</h3>
                <p>Waiting: <span className="count-value">{videoSseCounts.queue.length}</span></p>
            </div>
            )}
           {currentMode === "Idle Employee Tracker" && (
            <div className={`log-counts idle-counts status-${videoSseCounts.idle.status.toLowerCase()}`}>
                <h3>Idle Area Status</h3>
                <p>Status: <strong className="count-value">{videoSseCounts.idle.status}</strong>
                    {videoSseCounts.idle.status === "IDLE" && (<span className="idle-duration"> ({fmtIdle(videoSseCounts.idle.duration)})</span>)}
                    {videoSseCounts.idle.status === "OCCUPIED" && videoSseCounts.idle.duration > 0 && (<span className="idle-duration"> (Prev. Idle {fmtIdle(videoSseCounts.idle.duration)})</span>)}
                    {videoSseCounts.idle.status === "DISCONNECTED" && (<span className="idle-duration error-log"> (Conn. Lost)</span>)}
                </p>
            </div>
            )}
        </div>
      )}

      {currentMode === "Waiting Time Tracker" && ddbSourceId && (
        <div className="log-summaries-section">
          {console.log("[LiveLogs] [RENDER] Waiting Time Tracker section rendering with:", {
            currentMode,
            ddbSourceId,
            waitingTimeStats: {
              connected: waitingTimeStats.connected,
              connecting: waitingTimeStats.connecting,
              overall: waitingTimeStats.overall,
              zonesCount: waitingTimeStats.zones ? waitingTimeStats.zones.length : 'undefined',
              zones: waitingTimeStats.zones
            }
          })}
          <div className={`log-counts waiting-time-counts ${waitingTimeStats.connected ? 'connected' : waitingTimeStats.connecting ? 'connecting' : 'disconnected'}`}>
            <h3>
              <span className={`connection-indicator ${waitingTimeStats.connected ? '' : waitingTimeStats.connecting ? 'connecting' : 'disconnected'}`}></span>
              Live Waiting Time Statistics
              {waitingTimeStats.connecting && <span style={{marginLeft: '8px', fontSize: '0.8em'}}>Connecting...</span>}
            </h3>
            
            {waitingTimeStats.connected && (
              <>
                <div className="overall-stats">
                  <h4>Overall Statistics</h4>
                  <div className="overall-stats-grid">
                    <div className="stat-item">
                      <span className="stat-label">Total People</span>
                      <span className="stat-value">{waitingTimeStats.overall.total_people}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Waiting &gt;1min</span>
                      <span className="stat-value highlight">{waitingTimeStats.overall.people_waiting_over_threshold}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total Entries</span>
                      <span className="stat-value">{waitingTimeStats.overall.total_entries}</span>
                    </div>
                  </div>
                </div>

                {/* Debug zone display logic */}
                {console.log("[LiveLogs] [RENDER] Zone display check:", {
                  zones: waitingTimeStats.zones,
                  zonesLength: waitingTimeStats.zones ? waitingTimeStats.zones.length : 'undefined',
                  hasZones: waitingTimeStats.zones && waitingTimeStats.zones.length > 0
                })}
                
                {waitingTimeStats.zones && waitingTimeStats.zones.length > 0 ? (
                  <div className="zone-stats">
                    <h4>Zone Statistics ({waitingTimeStats.zones.length} zones)</h4>
                    <div className="zone-list-container">
                      {waitingTimeStats.zones.map((zone, index) => {
                        console.log(`[LiveLogs] [RENDER] Rendering zone ${index}:`, zone);
                        return (
                        <div key={zone.zone_id || index} className={`zone-item ${zone.status.toLowerCase()}`}>
                          <div className="zone-header">
                            <span className="zone-name">{zone.zone_name}</span>
                            <span className={`status-value status-${zone.status.toLowerCase()}`}>{zone.status}</span>
                          </div>
                          <div className="zone-content">
                            <div className="zone-metrics">
                              <div className="zone-metric">
                                <span className="metric-label">Current</span>
                                <span className="metric-value">{zone.current_people}</span>
                              </div>
                              <div className="zone-metric">
                                <span className="metric-label">Entries</span>
                                <span className="metric-value">{zone.total_entries}</span>
                              </div>
                              <div className="zone-metric">
                                <span className="metric-label">Waiting &gt;1min</span>
                              <span className="metric-value">{zone.waiting_over_1min}</span>
                            </div>
                            <div className="zone-metric">
                              <span className="metric-label">Avg Wait</span>
                              <span className="metric-value">{fmtWaitTime(zone.current_average_wait_time_seconds)}</span>
                            </div>
                            <div className="zone-metric">
                              <span className="metric-label">Session Avg</span>
                              <span className="metric-value">{fmtWaitTime(zone.average_wait_time_since_start_seconds)}</span>
                            </div>
                            <div className="zone-metric">
                              <span className="metric-label">Max Wait</span>
                              <span className="metric-value">{fmtWaitTime(zone.max_wait_time_seconds)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="zone-stats">
                    <h4>Zone Statistics</h4>
                    <div className="connection-status">
                      <span style={{color: '#6c757d'}}>
                        ℹ️ No zones configured or no zone data available. 
                        {waitingTimeStats.zones ? `(${waitingTimeStats.zones.length} zones found)` : '(zones array is null/undefined)'}
                      </span>
                    </div>
                  </div>
                )}

                {waitingTimeStats.metadata.last_updated && (
                  <div className="last-updated">
                    Last Updated: {format(new Date(waitingTimeStats.metadata.last_updated), "HH:mm:ss")}
                    {waitingTimeStats.metadata.session_duration && (
                      <> • Session: {waitingTimeStats.metadata.session_duration}</>
                    )}
                    {waitingTimeStats.metadata.camera_name && (
                      <> • Camera: {waitingTimeStats.metadata.camera_name}</>
                    )}
                  </div>
                )}
              </>
            )}

            {waitingTimeStats.connecting && (
              <div className="connection-status">
                <span style={{color: '#007bff'}}>🔄 Connecting to waiting time statistics...</span>
              </div>
            )}

            {!waitingTimeStats.connected && !waitingTimeStats.connecting && (
              <div className="connection-status">
                <span className="error-log">❌ Connection Status: Disconnected</span>
                <br />
                <small style={{color: '#6c757d', marginTop: '4px', display: 'block'}}>
                  Check if the camera is active and zones are configured properly.
                </small>
              </div>
            )}
          </div>
        </div>
      )}

      <h3
        className="logs-main-header"
        style={{
          marginTop: isDedicatedVideoLogMode && detailLogSseEventPattern && !isAnyStreamingAudioMode ? "1.5rem" : "0.5rem",
        }}
      >
        {headerText}
      </h3>

      <div className="logs-content-area" ref={logsContainerRef}>
        {mainContent}
      </div>
    </div>
  );
};


LiveLogs.defaultProps = {
  currentMode: "No Active Process",
  analysisResult: null,
  videoInstanceId: null,
  audioStreamId: null,
  audioTaskType: null,
  ddbSourceId: null,
  currentAnalysisMode: null,
  enableHistoricalPolling: true,
};

export default React.memo(LiveLogs);